import type { components, paths } from "@securityjourney/svc-auth/dist/gen/auth";

import { OpenApiFetchWrapper, type ServiceResponse } from "../openApiFetchWrapper";
import type { Scopes } from "./authTypes";

export type AccessTokenResponse =
  components["schemas"]["securityjourney.proto.auth.AccessTokenResponse"];

function scopeString(scopes: Scopes | undefined): string | undefined {
  if (!scopes) {
    return undefined;
  }

  const values: string[] = [];

  if (scopes.tenant) {
    values.push(`tenant-${scopes.tenant}`);
  }
  if (scopes.as) {
    values.push(`as-${scopes.as}`);
  }
  if (scopes.admin) {
    values.push(`admin-${scopes.admin}`);
  }
  if (scopes.sjAdmin) {
    values.push("sj-admin");
  }

  return values.length > 0 ? values.join(" ") : undefined;
}

// Exchanges a Cognito token for a short-lived service access token.
export class AuthClient extends OpenApiFetchWrapper<paths> {
  constructor() {
    // Token exchange endpoint requires no auth
    super({ noAuth: true });
  }

  async exchangeForAccessToken({
    subjectToken,
    scopes,
  }: {
    subjectToken?: string;
    scopes?: Scopes;
  }): Promise<ServiceResponse<AccessTokenResponse>> {
    const bodyData: Record<string, string | string[]> = {
      subject_token: subjectToken ?? "",
      subject_token_type: "urn:ietf:params:oauth:token-type:jwt",
      grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
    };

    const scope = scopeString(scopes);
    if (scope) {
      bodyData.scope = scope;
    }

    const params = asFormData(bodyData);
    const baseUrl = window.location.protocol + "//" + window.location.host;

    return this.postUrlEncoded<AccessTokenResponse>(baseUrl + "/svc/auth/token", params, {
      noAuth: true,
    });
  }
}

let authClientInstance: AuthClient | null = null;

export const getAuthClient = (): AuthClient => {
  if (!authClientInstance) {
    authClientInstance = new AuthClient();
  }
  return authClientInstance;
};

function asFormData(data: Record<string, string | string[]>): URLSearchParams {
  const form = new URLSearchParams();

  for (const [key, value] of Object.entries(data)) {
    if (Array.isArray(value)) {
      value.forEach(item => form.append(key, item));
    } else {
      form.append(key, value);
    }
  }

  return form;
}
